import { motion, useReducedMotion } from 'framer-motion'
import PropTypes from 'prop-types'

import { classNames } from '../../helpers'
import { EASE } from './tokens'

// Die drei Grundformen aus dem Aivy-Brandbook, flach und ohne Verlauf:
// Dreieck in Mango, Quadrat in Aquamarin, Kreis in Pink.

const SHAPES = {
  triangle: {
    className: 'fill-mango',
    node: <path d='M12 3.5 21 20H3z' />
  },
  square: {
    className: 'fill-aquaMarine',
    node: <rect x='4' y='4' width='16' height='16' rx='1.5' />
  },
  circle: {
    className: 'fill-carnationPink',
    node: <circle cx='12' cy='12' r='8.5' />
  }
}

const ORDER = ['triangle', 'square', 'circle']

export const BrandShape = ({ shape, className }) => (
  <svg
    viewBox='0 0 24 24'
    aria-hidden='true'
    className={classNames(SHAPES[shape].className, className)}
  >
    {SHAPES[shape].node}
  </svg>
)

BrandShape.propTypes = {
  shape: PropTypes.oneOf(ORDER).isRequired,
  className: PropTypes.string
}

BrandShape.defaultProps = {
  className: 'size-6'
}

// Arbeitsanzeige: Die Formen hüpfen nacheinander, solange gerechnet wird.
// Bei reduzierter Bewegung stehen sie still.
export const BrandShapesLoader = ({ className }) => {
  const reducedMotion = useReducedMotion()

  return (
    <div
      aria-hidden='true'
      className={classNames('flex items-end justify-center gap-2', className)}
    >
      {ORDER.map((shape, index) => (
        <motion.span
          key={shape}
          className='block'
          animate={
            reducedMotion
              ? undefined
              : { y: [0, -10, 0], rotate: shape === 'square' ? [0, 90, 90] : 0 }
          }
          transition={{
            duration: 0.9,
            ease: EASE.standard,
            repeat: Infinity,
            repeatDelay: 0.3,
            delay: index * 0.15
          }}
        >
          <BrandShape shape={shape} />
        </motion.span>
      ))}
    </div>
  )
}

BrandShapesLoader.propTypes = {
  className: PropTypes.string
}

BrandShapesLoader.defaultProps = {
  className: undefined
}

// Einmaliger Ausbruch der Formen um ein Element herum, etwa um den Haken nach
// einer abgeschlossenen Aktion. Liegt absolut über dem Elternelement, das
// relative sein muss. Bei reduzierter Bewegung erscheint nichts.
const BURST = Array.from({ length: 12 }, (_, index) => {
  const angle = (index / 12) * 2 * Math.PI - Math.PI / 2
  const distance = index % 2 === 0 ? 62 : 48
  return {
    shape: ORDER[index % ORDER.length],
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance,
    rotate: (index % 2 === 0 ? 1 : -1) * (60 + index * 20)
  }
})

export const BrandShapeBurst = () => {
  const reducedMotion = useReducedMotion()
  if (reducedMotion) return null

  return (
    <span
      aria-hidden='true'
      className='pointer-events-none absolute inset-0 flex items-center justify-center'
    >
      {BURST.map(({ shape, x, y, rotate }, index) => (
        <motion.span
          key={index}
          className='absolute block'
          initial={{ x: 0, y: 0, scale: 0, opacity: 1, rotate: 0 }}
          animate={{ x, y, scale: [0, 1, 0.6], opacity: [1, 1, 0], rotate }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE.out }}
        >
          <BrandShape shape={shape} className='size-3.5' />
        </motion.span>
      ))}
    </span>
  )
}
